import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { CountryItem } from 'src/components/country-bar/types';
import { deriveCountryCode } from 'src/components/visibility/hooks/countryCode';
import { type RegionsParam, useGetCampaignLeaksRegionsQuery } from 'src/context/api/campaignLeaks/api';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import type { RootState } from 'src/context/store';
import { useCampaignLeaksPlatform } from './platform-toggle';

/**
 * Regions sent to the endpoints: null ("All", campaigns without a region included) when every region is
 * checked, when nothing is checked yet, or while the selection does not match the platform's regions.
 */
export const regionsParam = (selected: string[], allNames: string[]): RegionsParam => {
  if (!allNames.length || !selected.length) return null;
  if (allNames.every((name) => selected.includes(name))) return null;
  if (selected.some((name) => !allNames.includes(name))) return null;
  return selected;
};

const NO_REGIONS: string[] = [];

/** Regions of the platform (by decreasing ad cost) and the selection of the region bar, shared by both tabs. */
export const useCampaignLeaksRegions = () => {
  const workspace: any = useCurrentWorkspace();
  const platform = useCampaignLeaksPlatform();
  const { currentData, isLoading, isFetching } = useGetCampaignLeaksRegionsQuery(
    { client: workspace?.bqTable, platform },
    { skip: !workspace?.bqTable }
  );
  const regions: CountryItem[] = useMemo(
    () =>
      (currentData?.regions ?? []).map((r) => ({
        country_name: r.region_name,
        country_code: deriveCountryCode(r.region_name),
      })),
    [currentData]
  );
  const allNames = useMemo(() => regions.map((r) => r.country_name), [regions]);
  const selected = useSelector((state: RootState) => state.filters.campaignLeaksRegions ?? NO_REGIONS);
  const param = useMemo(() => regionsParam(selected, allNames), [selected, allNames]);
  return { regions, allNames, selected, regionsParam: param, loading: isLoading || isFetching };
};
